import * as Sentry from "@sentry/react";
import { Bell, Clock, Inbox, LayoutDashboard, Mail, ScrollText, Users, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import {
    Avatar,
    AvatarGradientFallback,
    Mono,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarTrigger,
    Text,
    ThemeToggle,
    Toaster,
} from "@athena/ui";
import { CurrentUserDocument } from "@/graphql/auth.generated";
import { ErrorFallback } from "@/components/ErrorFallback";
import { ShellSwitch } from "@/components/ShellSwitch";
import { SidebarUserMenu } from "@/components/SidebarUserMenu";
import { useAlumniNavCounts } from "@/hooks/useAlumniNavCounts";
import {
    ALUMNI_ROOT,
    BROADCAST,
    CHAT,
    CLIENT_QUEUE,
    CONTACT_LOG,
    DASHBOARD,
    MY_PROFILE,
    PUSH_NOTIFICATIONS,
} from "@/lib/adminPilot";

interface NavItem {
    labelKey: "dashboard" | "directory" | "queue" | "log" | "broadcast" | "push" | "chat";
    icon: LucideIcon;
    path: string;
    activePaths: string[];
}

const NAV_ITEMS: NavItem[] = [
    { labelKey: "dashboard", icon: LayoutDashboard, path: DASHBOARD, activePaths: [DASHBOARD] },
    { labelKey: "directory", icon: Users, path: ALUMNI_ROOT, activePaths: [ALUMNI_ROOT] },
    { labelKey: "queue", icon: Clock, path: CLIENT_QUEUE, activePaths: [CLIENT_QUEUE] },
    { labelKey: "log", icon: ScrollText, path: CONTACT_LOG, activePaths: [CONTACT_LOG] },
    { labelKey: "broadcast", icon: Mail, path: BROADCAST, activePaths: [BROADCAST] },
    { labelKey: "push", icon: Bell, path: PUSH_NOTIFICATIONS, activePaths: [PUSH_NOTIFICATIONS] },
    { labelKey: "chat", icon: Inbox, path: CHAT, activePaths: [CHAT] },
];

/**
 * The item whose active path is the longest prefix of `pathname`, as `AppSidebar` matches but with one
 * difference: every console path starts with the root, so the directory's path matches everywhere and
 * the most specific match has to win. A profile (`/alumni/:clientId`) matches only the root.
 *
 * Matched by whole segment, so `/alumni/logan` is a profile and not the log. The coordinator's own
 * profile is not in the nav at all, so nothing is current there.
 */
const currentNavItem = (pathname: string): NavItem | undefined => {
    if (pathname === MY_PROFILE) return undefined;
    const within = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
    let best: { item: NavItem; length: number } | undefined;
    for (const item of NAV_ITEMS) {
        for (const path of item.activePaths) {
            if (within(path) && path.length > (best?.length ?? 0)) best = { item, length: path.length };
        }
    }
    return best?.item;
};

/**
 * The alumni coordinator console: what an ORG_ADMIN or above in a pilot organization sees in place
 * of the admin portal (see `AdminPilotGate`).
 *
 * The mockups' rail also lists Reports. It joins the nav when its screen is built, rather than as a
 * link to nothing.
 */
export function AlumniLayout() {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { data, loading } = useQuery(CurrentUserDocument);
    const current = currentNavItem(pathname);
    // Keyed by the items they badge; an item with no count, or a count of zero, has no badge.
    const counts: Partial<Record<NavItem["labelKey"], number>> = useAlumniNavCounts();

    return (
        <SidebarProvider className="alumni-shell">
            <Sidebar>
                <SidebarHeader className="p-4">
                    <div className="flex items-center gap-3">
                        <Avatar square size="sm" className="shrink-0">
                            <AvatarGradientFallback>RR</AvatarGradientFallback>
                        </Avatar>
                        <div className="min-w-0">
                            <Text size="sm" weight="semibold" className="font-display text-sidebar-foreground">
                                {t("admin.alumni.brandName")}
                            </Text>
                            <Mono size="2xs" uppercase className="text-sidebar-muted-foreground tracking-widest">
                                {t("admin.alumni.consoleName")}
                            </Mono>
                        </div>
                    </div>
                </SidebarHeader>
                <SidebarContent className="px-2">
                    <SidebarMenu>
                        {NAV_ITEMS.map((item) => {
                            const count = counts[item.labelKey];
                            return (
                                <SidebarMenuItem key={item.path}>
                                    <SidebarMenuButton asChild isActive={item === current}>
                                        <NavLink to={item.path}>
                                            <item.icon />
                                            <span>{t(`admin.alumni.nav.${item.labelKey}`)}</span>
                                        </NavLink>
                                    </SidebarMenuButton>
                                    {count ? (
                                        <SidebarMenuBadge className="bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground rounded-full px-1.5 text-xs">
                                            {count}
                                        </SidebarMenuBadge>
                                    ) : null}
                                </SidebarMenuItem>
                            );
                        })}
                    </SidebarMenu>
                </SidebarContent>
                <SidebarFooter className="p-4">
                    <ShellSwitch />
                    <SidebarUserMenu user={data?.currentUser} loading={loading} profilePath={MY_PROFILE} />
                </SidebarFooter>
            </Sidebar>
            <SidebarInset>
                <header className="bg-background border-border sticky top-0 z-40 flex h-12 items-center justify-between gap-2 border-b px-4">
                    <SidebarTrigger className="z-50 h-4 w-4" />
                    <ThemeToggle />
                </header>
                <main className="flex flex-col">
                    <Sentry.ErrorBoundary
                        fallback={({ error, resetError }) => <ErrorFallback error={error} resetError={resetError} />}
                    >
                        <Outlet />
                        <Toaster />
                    </Sentry.ErrorBoundary>
                </main>
            </SidebarInset>
        </SidebarProvider>
    );
}
